import { useMutation, useQueryClient } from '@tanstack/react-query';
import GeneralDialog from '../../../../../shared/general-dialog.tsx';
import { deleteSync, GPanelContactsKeys } from '../../gpanel-contacts-api.ts';
import ChangeLogDialog from '../../../../../shared/change-log-dialog.tsx';
import { useState } from 'react';
import { Messages } from '../../../../../utils/message-constants.ts';
import axios from 'axios';
import { ApiException } from '../../../../../../types.ts';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list.ts';
import { useAnalytics } from '../../../../../shared/analytics/create-events.ts';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../utils/roles/client-roles.ts';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import { handleErrors } from '../../../../../utils/errors.ts';
import { useStore } from '../../../../state/zustand-store.ts';
import { LabelSync } from '../../shared/types.ts';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export default function GPanelContactsDeleteSyncDialog({
  handleClose,
  sync,
}: {
  handleClose: () => void;
  sync: LabelSync;
}) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [deletePermanently, setDeletePermanently] = useState<boolean>(false);
  const { changeLogRoles } = useUserSettings();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteSync(sync.id, deletePermanently, changeLogMessage),
    onSuccess: () => {
      setChangeLogMessage('');
      setSnackbar({
        open: true,
        severity: deletePermanently ? 'info' : 'success',
        message: deletePermanently
          ? 'The task to delete the contact sync and permanently remove the Google Contacts and label has begun.'
          : Messages.Success.Deleted('Contact sync'),
      });

      if (!deletePermanently) {
        const updateCache = (oldData: LabelSync[] | undefined) =>
          oldData?.filter((s) => s.id !== sync.id) || [];

        queryClient.setQueryData<LabelSync[]>(
          [GPanelContactsKeys.AllSyncs],
          updateCache
        );
        queryClient.setQueryData<LabelSync[]>(
          [GPanelContactsKeys.LabelSyncs, String(sync.labelId)],
          updateCache
        );
      } else {
        const updateCache = (oldData: LabelSync[] | undefined) =>
          oldData?.map((s) => {
            if (s.id === sync.id) {
              return { ...s, status: 'DELETING' };
            }
            return s;
          }) || [];

        queryClient.setQueryData<LabelSync[]>(
          [GPanelContactsKeys.AllSyncs],
          updateCache
        );

        queryClient.setQueryData<LabelSync[]>(
          [GPanelContactsKeys.LabelSyncs, String(sync.labelId)],
          updateCache
        );
      }

      queryClient.invalidateQueries({
        queryKey: [GPanelContactsKeys.LabelGroups],
      });

      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message) {
          setErrorText(errorResponse.message);
        } else {
          handleErrors({
            error,
            snackbarMessage: Messages.Errors.Network.Deleting('contact'),
          });
        }
      }
    },
    onSettled: () => {
      createEvent(analytics.directory.gpanel_contacts.trash_contact, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.gpanelContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const handleDelete = async () => {
    if (
      hasPermission(
        changeLogRoles,
        GPanelContactsPermissions.DeleteContactSync.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  };

  return (
    <>
      <GeneralDialog
        open={true}
        handleClose={() => {
          handleClose();
        }}
        title={'Delete Contact Sync'}
        showErrorColorOnTitleUnderline={true}
        dialogContent={
          <Stack sx={{ px: 4 }} gap={2}>
            <Typography variant="body2">
              {`You are about to delete the contact sync '${sync.name}'. This action will immediately stop all future contact synchronization between gPanel and your targets' Google Contacts.`}
            </Typography>
            <Stack justifyContent="start">
              <FormControlLabel
                control={
                  <Checkbox
                    value={deletePermanently}
                    color="error"
                    onChange={(e) => setDeletePermanently(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    {`Permanently remove the '${sync.targetLabelName}' label and all associated contacts from targets' Google Contacts.`}
                  </Typography>
                }
              />
            </Stack>
            {errorText && <FormHelperText error children={errorText} />}
          </Stack>
        }
        primaryButton={{
          loading: isPending,
          disabled: isPending,
          action: handleDelete,
          color: 'error',
          title: 'Delete',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
